// frontend/src/pages/IngredientList.js
import React, { useEffect, useState } from 'react';
import IngredientCard from '../components/IngredientCard';
import { fetchWithCSRF } from '../utils/csrfUtils';
import supabase from '../supabaseClient';


function IngredientList() {
  const [ingredients, setIngredients] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const [foodGroups, setFoodGroups] = useState([]);
  const [selectedFoodGroups, setSelectedFoodGroups] = useState([]);
  const [sessionToken, setSessionToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // First useEffect to get the session token
  useEffect(() => {
    const getSessionToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setSessionToken(session.access_token);
        } else {
          console.error('No session token available');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        setIsLoading(false);
      }
    };
    getSessionToken();
  }, []);

  // Second useEffect for initial data fetching
  useEffect(() => {
    if (!sessionToken) return;

    const fetchInitialData = async () => {
      try {
        // Fetch food groups and ingredients in parallel
        const [foodGroupsResponse, ingredientsResponse] = await Promise.all([
          fetchWithCSRF('/api/v1/food-group', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          }),
          fetchWithCSRF('/api/v1/ingredient', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          })
        ]);

        const foodGroupsData = await foodGroupsResponse.json();
        const ingredientsData = await ingredientsResponse.json();

        if (Array.isArray(foodGroupsData)) {
          setFoodGroups(foodGroupsData);
        }
        if (Array.isArray(ingredientsData)) {
          setIngredients(ingredientsData);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [sessionToken]);

  // Fetch ingredients whenever selectedFoodGroups change
  useEffect(() => {
    if (!sessionToken) return;

    const fetchIngredientsByFoodGroups = async () => {
      try {
        if (selectedFoodGroups.length > 0) {
          const foodGroupIds = selectedFoodGroups.join(',');
          const response = await fetchWithCSRF(`/api/v1/ingredient-food-group/multiple_food_group_ids/${foodGroupIds}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });

          if (!response.ok) {
            throw new Error('Error fetching ingredients by food groups');
          }

          const data = await response.json();
          // Extract the unique ingredient IDs from the response
          const ingredientIds = [...new Set(data.map(item => item.ingredient_id))];

          if (ingredientIds.length > 0) {
            // Fetch ingredient details for the obtained ingredient IDs
            const ingredientResponse = await fetchWithCSRF('/api/v1/ingredient/multiple_ids', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
              },
              body: JSON.stringify({ ids: ingredientIds }),
            });

            if (!ingredientResponse.ok) {
              throw new Error('Error fetching ingredient details');
            }

            const ingredientData = await ingredientResponse.json();
            if (Array.isArray(ingredientData)) {
              setIngredients(ingredientData);
            } else {
              setIngredients([]);
            }
          } else {
            // No ingredients found for the selected food groups
            setIngredients([]);
          }
        } else {
          // If no food groups are selected, fetch all ingredients
          const response = await fetchWithCSRF('/api/v1/ingredient', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });

          if (!response.ok) {
            throw new Error('Error fetching all ingredients');
          }

          const data = await response.json();
          if (Array.isArray(data)) {
            setIngredients(data);
          } else {
            setIngredients([]);
          }
        }
      } catch (error) {
        console.error('Error fetching ingredients:', error);
        setIngredients([]);
      }
    };

    fetchIngredientsByFoodGroups();
  }, [selectedFoodGroups, sessionToken]);

  // Handle checkbox selection
  const handleFoodGroupChange = (foodGroupId) => {
    setSelectedFoodGroups(prevSelected => 
      prevSelected.includes(foodGroupId)
        ? prevSelected.filter(id => id !== foodGroupId)
        : [...prevSelected, foodGroupId]
    );
  };

  // Filter ingredients based on search query
  const filteredIngredients = ingredients.filter(ingredient =>
    ingredient.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="ingredient-list container">
      <h1>All Ingredients</h1>
      
      {/* Search input */}
      <input
        type="text"
        placeholder="Search for ingredient..."
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        className="form-control mb-4 ingredient-search-input"
      />
      <a href="create-ingredient" className="mb-4 d-inline-block">Create Ingredient</a>
      
      {/* Food Group Buttons */}
      <div className="cuisine-filters mb-4">
        {foodGroups.map(foodGroup => (
          <button
            key={foodGroup.id}
            className={`btn ${
              selectedFoodGroups.includes(foodGroup.id) 
                ? 'btn-primary active' 
                : 'btn-outline-primary'
            } me-2 mb-2`}
            onClick={() => handleFoodGroupChange(foodGroup.id)}
            type="button"
          >
            {foodGroup.name}
          </button>
        ))}
      </div>

      {/* Ingredients Grid */}
      <div className="row g-3">
        {filteredIngredients.map(ingredient => (
          <div key={ingredient.id} className="col-6 col-md-4 col-lg-3">
            <div className="border rounded p-2 h-100">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-1 flex-grow-1 text-center">{ingredient.name}</h6>
                <button className="btn btn-sm">
                  <img src="/icons/plus.svg" alt="Add to cart" />
                </button>
              </div>
              <small className="text-muted d-block">
                {ingredient.foodGroups?.join(', ')}
              </small>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default IngredientList;
